import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Form } from "react-bootstrap";
import MemberIconNew from "../member-icon-new/member-icon-new";
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Tooltip from 'react-bootstrap/Tooltip';
import { GroupIcon } from '../../assets/icons.jsx';
import "./member-dropdown-project.scss";
import MemberIconMultipleOwner from "../member-icon-new/member-icon-multi-owner.jsx";
import { unavailableReason } from "../TaskAssignMember/TaskAssignMemberHelper";

const MemberDropdownProject = ({
  index,
  member,
  type,
  filter,
  toggleRow,
  selectedMembers,
  rowFilter,
  taskType,
  projects,
  isGroup,
  classNameAddition,
  isGroupOwner = false,
  isUserAdminComponent = false,
  ownerId,
  IsMultiOwner = false,
}) => {
  const [className] = useState(classNameAddition ? classNameAddition : '');
  const [checked, setChecked] = useState(false);
  const [name, setName] = useState("");
  // Archived/moved member or group (TAD-5898): grey the row. The flag is only
  // ever set on project payloads for unavailable entities, so other consumers
  // are unaffected.
  const isArchived =
    member?.User?.Archived === true || member?.User?.Deleted === true || member?.Archived === true;

  useEffect(() => {
    const { User = {}, Name, UserNameFull } = member;
    const isChecked = selectedMembers.some((selectedMember) => selectedMember.UserId === member.UserId);
    setChecked(isChecked);
    if (selectedMembers.length === 0) setChecked(false);

    const newName = truncateName(User.Name || Name || UserNameFull);
    setName(newName);
  }, [member, selectedMembers]);

  const truncateName = (name) => {
    if (name.length > 30) return `${name.slice(0, 27)}...`;
    return name;
  };

  const overlayString = () => {
    const { GroupId, strRole, StrRole, User = {}, emailID, Email } = member;
    const email = User.Email || Email ||  emailID || "";
    const role = strRole || StrRole || member?.User?.Role?.Name || member?.Role?.Name || "";
    const company = member?.User?.Company?.Name || member?.Company?.Name || member?.CompanyName || "";

    if (GroupId && GroupId > 0) return groupHover();

    let result = "";
    if (name?.length > 0) result += ` (${name})`;
    if (role?.length > 0) result += ` (${role})`;
    if (company?.length > 0) result += ` - ${company}`;
    if (!projects && email?.length > 0) result += ` - ${email}`;
    return result;
  };

  const groupHover = () => {
    if (member.GroupId && member.GroupId !== null) {
      if (member.GroupHover || isGroup) {
        return member.GroupHover;
      } else {
        let hoverStr = "";
        (member.Members || []).forEach((groupMember) => {
          const { StrRole, User = {}, Email, CompanyName } = groupMember;
          const name = User?.Name || groupMember?.Name || "";
          const email = User.Email || Email || "";
          const role = StrRole || User?.Role?.Name || User?.StrRole || "";
          const company = User?.CompanyName || groupMember?.Company?.Name || CompanyName || "";

          let memberInfo = [name, role, company].filter(Boolean).join(" - ");
          if (!projects && email)  memberInfo += ` - ${email}`;
          hoverStr += memberInfo + "\n"; 
        });
        return hoverStr.trim(); 
      }
    } else {
      return "";
    }
  };
  const formatName = () => {
    const maxLength = type === "user" && !filter ? 6 : 12;
    return name.length <= maxLength ? name : name.substring(0, maxLength) + "...";
  };

  const toggleChecked = (e) => {
    if (member.IsOwner || ownerId === member.UserId) {
      return; 
    }
    toggleRow(member, !checked);
    setChecked(!checked);
  };

  const renderMemberIcon = () => {
    return (
      IsMultiOwner ?
      <MemberIconMultipleOwner
        type={type}
        member={member}
        isGroup={isGroupOwner}
      /> :
      <MemberIconNew
        type={type}
        member={member}
        extraMembers={0}
        isGroup={isGroupOwner}
      />
    );
  };

  const renderOverlayTrigger = () => {
    const rowContent = (
      <div className={`member-dropdown-row-name${className}${isArchived ? " member-dropdown-row-archived" : ""}`}>
        {formatName()} {(type === "user" && !filter) ? "(Owner)" : ""}
        {member.GroupId && <img className='group-icon' alt='group icon' src={GroupIcon}/>}
      </div>
    );

    // Archived rows explain themselves; an empty hover (archived group whose
    // roster was fully revoked) must not render a blank tooltip, which shows
    // up as a black dot (QA feedback, TAD-5898).
    const overlayContent = isArchived
      ? unavailableReason({ Archived: true, Deleted: member?.User?.Deleted === true, IsGroup: Boolean(member.GroupId || member.IsGroup) })
      : overlayString();
    if (!overlayContent) {
      return rowContent;
    }

    return (
      <OverlayTrigger placement="right" delay={{ show: 150, hide: 150 }} trigger={['hover', 'focus']} overlay={
        <Tooltip id={`tooltip-${index}`} >
          {overlayContent}
        </Tooltip>
      }>
        {rowContent}
      </OverlayTrigger>
    );
  };

  const renderMemberInfo = () => {
    return (
      <React.Fragment>
        <div className='member-dropdown-info'>
          {member?.User?.Email || member?.Email || ""}
        </div>
        <div className='member-dropdown-info'>
          {member?.User?.Company?.Name || member?.Company?.Name || member?.CompanyName || ""}
        </div>
      </React.Fragment>
    );
  };

  return (
    <div>
      <Form.Check className={`${!projects ? 'member-dropdown-row' : 'member-dropdown-row-small'}${className}`}>
        { filter &&
          <Form.Check.Input
            className={`member-dropdown-row-checkbox${className}`}
            type="checkbox"
            checked={checked}
            disabled={!filter}
            onClick={(e) => toggleChecked(e)}
          />
        }
        <div className='member-name-icon-container'>
          <div className={`member-dropdown-row-user-icon${className}`}>
            {renderMemberIcon()}
          </div>
          <div className='member-name-email-department-container'>
            {renderOverlayTrigger()}
            { projects && renderMemberInfo() }
          </div>
        </div>
      </Form.Check>
    </div>
  );
};

MemberDropdownProject.propTypes = {
  index: PropTypes.number.isRequired,
  member: PropTypes.shape({
    UserId: PropTypes.number,
    Name: PropTypes.string,
    Email: PropTypes.string,
    Id: PropTypes.number,
    IsOwner: PropTypes.bool,
    strRole: PropTypes.string,
    StrRole: PropTypes.string,
    Initials: PropTypes.string,
    GroupId: PropTypes.number,
    emailID: PropTypes.string,
    Archived: PropTypes.bool,
    User: PropTypes.shape({
      Name: PropTypes.string.isRequired,
      Email: PropTypes.string.isRequired,
      Id: PropTypes.number.isRequired,
      strRole: PropTypes.string,
      Initials: PropTypes.string.isRequired,
      Archived: PropTypes.bool,
      Deleted: PropTypes.bool,
      Company: PropTypes.shape({
        Name: PropTypes.string.isRequired,
        Id: PropTypes.number.isRequired,
      }),
      Role: PropTypes.shape({
        Id: PropTypes.number,
        Active: PropTypes.bool,
        Admin: PropTypes.bool,
        Company: PropTypes.number,
        CompanyName: PropTypes.string,
        CreatedBy: PropTypes.number,
        Created: PropTypes.any,
        Description: PropTypes.string,
        Modified: PropTypes.any,
        ModifiedById: PropTypes.number,
        Name: PropTypes.string,
        SuperAdmin: PropTypes.bool,
      })
    }),
    Group: PropTypes.shape({
      Members: PropTypes.arrayOf(
        PropTypes.shape({
          User: PropTypes.shape({
            Name: PropTypes.string.isRequired
          }).isRequired
        })
      )
    }),
    permission: PropTypes.string,
    Role: PropTypes.shape({
      Name: PropTypes.string
    }),
    Company: PropTypes.shape({
      Name: PropTypes.string,
      Id: PropTypes.number,
    }),
    GroupHover: PropTypes.string,
    Members: PropTypes.arrayOf(
      PropTypes.shape({
        StrRole: PropTypes.string,
        User: PropTypes.shape({
          Name: PropTypes.string,
          Email: PropTypes.string,
          Role: PropTypes.shape({
            Name: PropTypes.string
          })
        }),
        Email: PropTypes.string,
        CompanyName: PropTypes.string
      })
    ),
    CompanyName: PropTypes.string,
    IsAssigned: PropTypes.bool,
    CanEdit: PropTypes.bool,
    CanView: PropTypes.bool,
    CanApprove: PropTypes.bool,
    CanPublish: PropTypes.bool,
    owner: PropTypes.bool
  }).isRequired,
  type: PropTypes.string.isRequired,
  filter: PropTypes.bool.isRequired,
  toggleRow: PropTypes.func.isRequired,
  selectedMembers: PropTypes.arrayOf(
    PropTypes.shape({
      Id: PropTypes.number,
      UserId: PropTypes.number,
    })
  ).isRequired,
  rowFilter: PropTypes.any,
  taskType: PropTypes.any,
  projects: PropTypes.bool.isRequired,
  isGroup: PropTypes.bool,
  classNameAddition: PropTypes.string,
  isGroupOwner: PropTypes.bool,
  isUserAdminComponent: PropTypes.bool,
  ownerId: PropTypes.number,
  IsMultiOwner: PropTypes.bool,
};


export default MemberDropdownProject;
